import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import { ChevronDown } from 'lucide-react'
import {
	Button,
	DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@rentflowdev/ui'
import { useListExternalListingsQuery, useListPropertiesQuery } from '@/api'
import type { Manager, Property, PropertyFilters, PropertyPublication } from '@/types'
import { isPublishedLikeStatus, normalizeListingPlatform, normalizeListingStatus } from '@/lib/external-listing-status'
import { AddPropertyDialog } from '@/components/add-property-dialog'
import { AddPropertyFromAvitoDialog } from '@/components/add-property-from-avito-dialog'
import { PropertiesEmptyState } from '@/components/properties-empty-state'
import { PropertiesPageCentered, PropertiesPageLoading } from '@/components/properties-page-centered'
import { PropertiesFilters } from '@/components/properties-filters'
import { PropertiesTable } from '@/components/properties-table'
import { PanelDown } from '@/components/panel-down'
import { useCan } from '@/lib/access'

export const Route = createFileRoute('/_app/properties/registry')({
	validateSearch: z.object({ status: z.string().optional() }),
	component: PropertiesRegistryPage,
})

const PAGE_SIZE = 10

// "Реестр" covers ready-to-rent properties only — DRAFT/ARCHIVED live in
// their own tabs. Rented and published are independent axes (a property can
// be both at once), so both surface as in-table filters, not separate tabs.
const STATUS_OPTIONS: Property['status'][] = ['AVAILABLE', 'RENTED']

const DEFAULT_FILTERS: PropertyFilters = {
	type: '',
	status: '',
	manager: '',
	platform: '',
	published: '',
	search: '',
}

function PropertiesRegistryPage() {
	const { t } = useTranslation()
	const canCreate = useCan('create', 'Property')
	const { status: initialStatus } = Route.useSearch()
	const [page, setPage] = useState(1)
	const [filters, setFilters] = useState<PropertyFilters>({
		...DEFAULT_FILTERS,
		status: STATUS_OPTIONS.find((s) => s === initialStatus) ?? '',
	})
	const [emptyManualOpen, setEmptyManualOpen] = useState(false)
	const [emptyFromPubOpen, setEmptyFromPubOpen] = useState(false)

	// Backend can only filter properties by one status at a time — fetch
	// AVAILABLE and RENTED separately and merge, same as the old available/
	// rented pages did individually.
	const { data: availableData, isLoading: isLoadingAvailable, error: availableError } = useListPropertiesQuery({
		status: 'AVAILABLE',
		limit: 100,
		...(filters.search && { search: filters.search }),
	})
	const { data: rentedData, isLoading: isLoadingRented, error: rentedError } = useListPropertiesQuery({
		status: 'RENTED',
		limit: 100,
		...(filters.search && { search: filters.search }),
	})
	// Cheap portfolio probe (any status, no search): the global onboarding empty
	// state must only appear when the account truly has zero properties, not when
	// e.g. the whole portfolio is DRAFT/ARCHIVED or a search returned nothing.
	const { data: anyPropertiesData, isLoading: isLoadingAny, error: anyError } = useListPropertiesQuery({ limit: 1 })
	// "Published" isn't a property status — it means there's a real listing on
	// a platform, which lives in the integration module. Fetch listings once
	// and join client-side; also fixes property.publications never being
	// populated by the backend (PropertiesTable's publication column always
	// showed "Нет" before this).
	const { data: listingsData, isLoading: isLoadingListings } = useListExternalListingsQuery({
		limit: 200,
	})

	const handleFilterChange = useCallback((patch: Partial<PropertyFilters>) => {
		setFilters((prev) => {
			const next = { ...prev, ...patch }
			// The platform select is only rendered while published === 'PUBLISHED';
			// clear it otherwise so a hidden platform value can't silently filter
			// the list down to nothing.
			if (next.published !== 'PUBLISHED') next.platform = ''
			return next
		})
		setPage(1)
	}, [])

	const allProperties = useMemo(() => {
		// A status flip between the two queries' refetch windows can briefly put
		// the same property into both lists — dedupe by id (later list wins).
		const byId = new Map<string, Property>()
		for (const p of [...(availableData?.properties ?? []), ...(rentedData?.properties ?? [])]) {
			byId.set(p.id, p)
		}
		return [...byId.values()]
	}, [availableData, rentedData])

	const listingsByProperty = useMemo(() => {
		const map = new Map<string, PropertyPublication[]>()
		for (const l of listingsData?.listings ?? []) {
			if (!isPublishedLikeStatus(l.status)) continue
			const pubs = map.get(l.propertyId) ?? []
			pubs.push({ platform: normalizeListingPlatform(l.platform), status: normalizeListingStatus(l.status) })
			map.set(l.propertyId, pubs)
		}
		return map
	}, [listingsData])

	const enrichedProperties: Property[] = useMemo(
		() => allProperties.map((p) => ({
			...p,
			publications: listingsByProperty.get(p.id) ?? [],
		})),
		[allProperties, listingsByProperty],
	)

	const uniqueManagers: Manager[] = enrichedProperties
		.map((p) => p.manager)
		.filter((m): m is Manager => !!m)
		.filter((m, i, arr) => arr.findIndex((x) => x.id === m.id) === i)

	const q = filters.search.trim().toLowerCase()
	const filtered = enrichedProperties
		.filter((p) => !filters.type || p.type === filters.type)
		.filter((p) => !filters.status || p.status === filters.status)
		.filter((p) => {
			if (!filters.manager) return true
			if (filters.manager === 'unassigned') return !p.manager
			return p.manager?.id === filters.manager
		})
		.filter((p) => {
			if (!filters.published) return true
			const isPublished = listingsByProperty.has(p.id)
			return filters.published === 'PUBLISHED' ? isPublished : !isPublished
		})
		.filter((p) =>
			// Platform only narrows the published view — ignore it otherwise
			// (it is also cleared on published change; this is defense in depth).
			filters.published !== 'PUBLISHED'
			|| !filters.platform
			|| (listingsByProperty.get(p.id)?.some((pub) => pub.platform === filters.platform) ?? false))
		.filter((p) => {
			if (!q) return true
			return p.city.toLowerCase().includes(q)
				|| p.address.toLowerCase().includes(q)
				|| p.id.toLowerCase().includes(q)
				|| p.manager?.name.toLowerCase().includes(q)
		})

	const properties = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

	// Block only while there's no data yet for the current query args — a search
	// keystroke re-triggers isLoading too (new args, no cache entry), but by then
	// we already have a prior result to keep showing instead of wiping the table.
	if (
		(isLoadingAvailable && !availableData) ||
		(isLoadingRented && !rentedData) ||
		(isLoadingListings && !listingsData) ||
		(isLoadingAny && !anyPropertiesData)
	) {
		return <PropertiesPageLoading />
	}

	// Full-page error only when a required query failed with nothing to show —
	// a transient refetch failure must not wipe an already-rendered table. A
	// listings-only failure is non-fatal: the table still renders, just with
	// empty publication badges.
	if (
		(availableError && !availableData) ||
		(rentedError && !rentedData) ||
		(anyError && !anyPropertiesData && !allProperties.length)
	) {
		return (
			<PropertiesPageCentered>
				<PropertiesEmptyState title={t('properties.loadErrorTitle')} description={t('properties.loadErrorDescription')} />
			</PropertiesPageCentered>
		)
	}

	const hasAnyProperty =
		(anyPropertiesData?.total ?? 0) > 0
		|| (anyPropertiesData?.properties.length ?? 0) > 0
		|| allProperties.length > 0

	if (!hasAnyProperty) {
		return (
			<>
				<PropertiesPageCentered>
					<PropertiesEmptyState
						title={t('properties.emptyTitle')}
						description={t('properties.emptyDescription')}
						actionSlot={canCreate && (
							<DropdownMenu>
								<DropdownMenuTrigger
									render={
										<Button iconRight={<ChevronDown className="size-4" />}>
											{t('properties.addProperty')}
										</Button>
									}
								/>
								<DropdownMenuContent align="center" className="min-w-40">
									<DropdownMenuItem onClick={() => setEmptyManualOpen(true)}>
										{t('properties.addManually')}
									</DropdownMenuItem>
									<DropdownMenuItem onClick={() => setEmptyFromPubOpen(true)}>
										{t('properties.importFromAvito')}
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
						)}
					/>
				</PropertiesPageCentered>
				<AddPropertyDialog open={emptyManualOpen} onOpenChange={setEmptyManualOpen} />
				<AddPropertyFromAvitoDialog open={emptyFromPubOpen} onOpenChange={setEmptyFromPubOpen} />
			</>
		)
	}

	// With an active search the server-side query itself can come back empty;
	// fall through to the filter empty state so the search input stays mounted.
	if (!allProperties.length && !filters.search) {
		return (
			<PropertiesPageCentered>
				<PropertiesEmptyState title={t('properties.empty.registryTitle')} description={t('properties.empty.registryDescription')} />
			</PropertiesPageCentered>
		)
	}

	if (!filtered.length) {
		return (
			<div className="flex h-full flex-col">
				<PropertiesFilters
					filters={filters}
					onFilterChange={handleFilterChange}
					managers={uniqueManagers}
					showStatus
					showPublished
					showPlatform={filters.published === 'PUBLISHED'}
					statusOptions={STATUS_OPTIONS}
				/>
				<PropertiesEmptyState title={t('properties.emptyFilterTitle')} description={t('properties.emptyFilterDescription')} />
			</div>
		)
	}

	return (
		<div className="flex h-full flex-col">
			<PropertiesFilters
				filters={filters}
				onFilterChange={handleFilterChange}
				managers={uniqueManagers}
				showStatus
				showPublished
				showPlatform={filters.published === 'PUBLISHED'}
				statusOptions={STATUS_OPTIONS}
			/>
			<PropertiesTable properties={properties} />
			<PanelDown total={filtered.length} page={page} onPageChange={setPage} limit={PAGE_SIZE} />
		</div>
	)
}
